import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const timeSeriesData = [
  { month: 'Jul', fatalities: 0, lti: 2, major: 3, minor: 8, nearMiss: 14 },
  { month: 'Aug', fatalities: 0, lti: 1, major: 4, minor: 7, nearMiss: 18 },
  { month: 'Sep', fatalities: 0, lti: 2, major: 2, minor: 9, nearMiss: 16 },
  { month: 'Oct', fatalities: 0, lti: 1, major: 3, minor: 6, nearMiss: 21 },
  { month: 'Nov', fatalities: 0, lti: 1, major: 2, minor: 5, nearMiss: 19 },
  { month: 'Dec', fatalities: 0, lti: 0, major: 2, minor: 4, nearMiss: 22 },
  { month: 'Jan', fatalities: 0, lti: 1, major: 1, minor: 5, nearMiss: 20 },
];

const pieData = [
  { name: 'Near Miss', value: 130, color: '#f59e0b' },
  { name: 'Minor Injury', value: 44, color: '#3b82f6' },
  { name: 'Major Injury', value: 17, color: '#f97316' },
  { name: 'LTI', value: 8, color: '#ef4444' },
  { name: 'Fatality', value: 0, color: '#7f1d1d' },
];

const RADIAN = Math.PI / 180;
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.04) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100)?.toFixed(0)}%`}
    </text>
  );
};

const BehaviouralSafetySection = () => {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-6 bg-blue-500 rounded-full" />
        <h2 className="text-base font-semibold text-foreground">Behavioural Safety</h2>
        <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">Last 7 months</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Series */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Incident Trend</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeSeriesData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="nearMiss" stroke="#f59e0b" strokeWidth={2} dot={false} name="Near Miss" />
              <Line type="monotone" dataKey="minor" stroke="#3b82f6" strokeWidth={2} dot={false} name="Minor" />
              <Line type="monotone" dataKey="major" stroke="#f97316" strokeWidth={2} dot={false} name="Major" />
              <Line type="monotone" dataKey="lti" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="LTI" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* Pie Chart */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Incident Type Breakdown (YTD)</p>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="60%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" labelLine={false} label={renderCustomLabel}>
                  {pieData?.map((entry, i) => <Cell key={i} fill={entry?.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-2">
              {pieData?.map((d) => (
                <div key={d?.name} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: d?.color }} />
                  <span className="text-xs text-muted-foreground">{d?.name}</span>
                  <span className="text-xs font-semibold text-foreground ml-auto pl-2">{d?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehaviouralSafetySection;
